import {
  forwardRef, useCallback, useEffect, useId, useLayoutEffect, useRef, useState,
  type FormEvent, type InputHTMLAttributes, type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { formatBusinessDay, parseBusinessDayInput, resolveBusinessDayFormat } from '../contracts/business-day';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { useBusinessNumber } from '../hooks/useBusinessNumber';
import { translate, useLocaleOrDefault } from '../i18n';
import { Modal } from './Modal';

export interface DateInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'defaultValue' | 'onChange' | 'min' | 'max'
> {
  readonly type?: 'date' | 'datetime-local';
  readonly value?: string;
  readonly defaultValue?: string;
  readonly min?: string;
  readonly max?: string;
  readonly onValueChange?: (value: string) => void;
}

/** noValidate forms keep their own other validators, but may not skip date errors. */
export function validateDateSubmission(event: FormEvent<HTMLFormElement>): void {
  const invalid = Array.from(event.currentTarget.querySelectorAll<HTMLInputElement>('input[data-date-format]'))
    .find((element) => !element.checkValidity());
  if (!invalid) return;
  event.preventDefault();
  event.stopPropagation();
  invalid.reportValidity();
}

function displayed(value: string, format: string, locale: string) {
  const [day, time = ''] = value.split('T');
  return { day: day ? formatBusinessDay(day, format, locale) : '', time };
}

function calendarDate(day: string): Date {
  const [year, month, date] = day.split('-').map(Number);
  const at = new Date(0);
  at.setFullYear(year, month - 1, date);
  at.setHours(12, 0, 0, 0);
  return at;
}

function calendarDay(at: Date): string {
  return `${String(at.getFullYear()).padStart(4, '0')}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(at.getDate()).padStart(2, '0')}`;
}

function shiftedMonth(day: string, delta: number): string {
  const at = calendarDate(day);
  const date = at.getDate();
  at.setDate(1);
  at.setMonth(at.getMonth() + delta);
  const last = new Date(at);
  last.setMonth(last.getMonth() + 1, 0);
  at.setDate(Math.min(date, last.getDate()));
  return calendarDay(at);
}

function Calendar({ selected, min, max, onSelect }: {
  readonly selected: string;
  readonly min?: string;
  readonly max?: string;
  readonly onSelect: (day: string) => void;
}) {
  const locale = useLocaleOrDefault();
  const { dateFormat } = useDisplaySettings();
  const { number } = useBusinessNumber();
  const bounded = (day: string) => min && day < min ? min : max && day > max ? max : day;
  const [cursor, setCursor] = useState(() => bounded(selected || calendarDay(new Date())));
  const focus = useRef<HTMLButtonElement>(null);
  const at = calendarDate(cursor);
  const month = cursor.slice(0, 7);
  const first = new Date(at);
  first.setDate(1);
  const last = new Date(first);
  last.setMonth(last.getMonth() + 1, 0);
  const weeks = Math.ceil((first.getDay() + last.getDate()) / 7);
  const title = `${new Intl.DateTimeFormat(locale, { month: 'long' }).format(first)} ${String(first.getFullYear()).padStart(4, '0')}`;
  const allowed = (day: string) =>
    parseBusinessDayInput(day, 'YYYY-MM-DD', locale) !== null &&
    (!min || day >= min) && (!max || day <= max);

  useEffect(() => { focus.current?.focus(); }, [cursor]);

  function keyDown(event: KeyboardEvent<HTMLButtonElement>) {
    let next = new Date(at);
    switch (event.key) {
      case 'ArrowLeft': next.setDate(next.getDate() - 1); break;
      case 'ArrowRight': next.setDate(next.getDate() + 1); break;
      case 'ArrowUp': next.setDate(next.getDate() - 7); break;
      case 'ArrowDown': next.setDate(next.getDate() + 7); break;
      case 'Home': next.setDate(next.getDate() - next.getDay()); break;
      case 'End': next.setDate(next.getDate() + 6 - next.getDay()); break;
      case 'PageUp': next = calendarDate(shiftedMonth(cursor, event.shiftKey ? -12 : -1)); break;
      case 'PageDown': next = calendarDate(shiftedMonth(cursor, event.shiftKey ? 12 : 1)); break;
      default: return;
    }
    event.preventDefault();
    const day = calendarDay(next);
    if (allowed(day)) setCursor(day);
  }

  return (
    <>
      <div className="tl-date-calendar__navigation">
        <button type="button" aria-label={translate(locale, 'dateInput.previousMonth')}
          disabled={month === '0001-01' || (!!min && shiftedMonth(cursor, -1).slice(0, 7) < min.slice(0, 7))}
          onClick={() => setCursor(bounded(shiftedMonth(cursor, -1)))}>&lt;</button>
        <strong aria-live="polite">{title}</strong>
        <button type="button" aria-label={translate(locale, 'dateInput.nextMonth')}
          disabled={month === '9999-12' || (!!max && shiftedMonth(cursor, 1).slice(0, 7) > max.slice(0, 7))}
          onClick={() => setCursor(bounded(shiftedMonth(cursor, 1)))}>&gt;</button>
      </div>
      <div role="grid" aria-label={title}>
        <div role="row" className="tl-date-calendar__week">
          {Array.from({ length: 7 }, (_, day) => {
            const weekday = new Date(0);
            weekday.setDate(weekday.getDate() + day - weekday.getDay());
            return <span role="columnheader" key={day}>
              {new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(weekday)}
            </span>;
          })}
        </div>
        {Array.from({ length: weeks }, (_, week) => (
          <div role="row" className="tl-date-calendar__week" key={week}>
            {Array.from({ length: 7 }, (_, weekday) => {
              const day = week * 7 + weekday - first.getDay() + 1;
              if (day < 1 || day > last.getDate()) return <span role="gridcell" key={weekday} />;
              const date = `${month}-${String(day).padStart(2, '0')}`;
              return <span role="gridcell" aria-selected={date === selected} key={weekday}>
                <button type="button" ref={date === cursor ? focus : undefined}
                  tabIndex={date === cursor ? 0 : -1} disabled={!allowed(date)}
                  aria-label={formatBusinessDay(date, dateFormat, locale)}
                  onKeyDown={keyDown} onClick={() => onSelect(date)}>{number(day)}</button>
              </span>;
            })}
          </div>
        ))}
      </div>
    </>
  );
}

/** Tenant notation in the editor; canonical ISO values in callbacks and forms. */
export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(function DateInput({
  type = 'date', value, defaultValue = '', onValueChange, name, id,
  min, max, step, required, disabled, readOnly, style, className,
  onBlur, onInvalid, 'aria-describedby': describedBy, ...rest
}, forwardedRef) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const calendarId = `${inputId}-calendar`;
  const errorId = `${inputId}-date-error`;
  const locale = useLocaleOrDefault();
  const { dateFormat } = useDisplaySettings();
  const format = resolveBusinessDayFormat(dateFormat);
  const [internal, setInternal] = useState(defaultValue);
  const canonical = value === undefined ? internal : value;
  const initial = displayed(canonical, format, locale);
  const [day, setDay] = useState(initial.day);
  const [time, setTime] = useState(initial.time);
  const [message, setMessage] = useState('');
  const [touched, setTouched] = useState(false);
  const [open, setOpen] = useState(false);
  const [fieldName, setFieldName] = useState('');
  const input = useRef<HTMLInputElement | null>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const lastEmitted = useRef(canonical);
  const current = useRef(canonical);
  current.current = canonical;
  const previous = useRef({ format, locale });
  const parsed = parseBusinessDayInput(day, format, locale);
  const wire = parsed && (type === 'date' || time)
    ? type === 'date' ? parsed : `${parsed}T${time}`
    : '';

  useLayoutEffect(() => {
    const labelled = rest['aria-labelledby']?.split(/\s+/)
      .map((key) => document.getElementById(key)?.textContent ?? '').join(' ').trim();
    const labels = Array.from(input.current?.labels ?? []).map((label) => {
      const copy = label.cloneNode(true);
      if (!(copy instanceof HTMLElement)) return '';
      copy.querySelectorAll('.tl-date-input, [aria-hidden="true"]').forEach((child) => child.remove());
      return copy.textContent?.trim() ?? '';
    }).join(' ');
    const next = labelled || rest['aria-label'] || labels;
    if (next !== fieldName) setFieldName(next);
  });

  useLayoutEffect(() => {
    const external = canonical !== lastEmitted.current;
    const changed = format !== previous.current.format || locale !== previous.current.locale;
    if (external) {
      const next = displayed(canonical, format, locale);
      setDay(next.day); setTime(next.time);
    } else if (changed) {
      const draft = parseBusinessDayInput(day, previous.current.format, previous.current.locale);
      if (draft) setDay(formatBusinessDay(draft, format, locale));
    }
    previous.current = { format, locale };
    lastEmitted.current = canonical;
  }, [canonical, format, locale, day]);

  useEffect(() => {
    if (disabled || readOnly) setOpen(false);
  }, [disabled, readOnly]);

  useEffect(() => {
    const form = input.current?.form;
    if (!form) return;
    const reset = (event: Event) => queueMicrotask(() => {
      if (event.defaultPrevented) return;
      const nextValue = value === undefined ? defaultValue : current.current;
      if (value === undefined) setInternal(nextValue);
      lastEmitted.current = nextValue;
      const next = displayed(nextValue, format, locale);
      setDay(next.day); setTime(next.time); setTouched(false);
    });
    form.addEventListener('reset', reset);
    return () => form.removeEventListener('reset', reset);
  }, [value === undefined, defaultValue, format, locale]);

  useLayoutEffect(() => {
    const native = document.createElement('input');
    native.type = type;
    native.required = !!required;
    if (min) native.min = min;
    if (max) native.max = max;
    if (step !== undefined) native.step = String(step);
    native.defaultValue = canonical;
    native.value = wire;
    let error = '';
    if ((day.trim() || time) && !parsed) error = translate(locale, 'dateInput.invalidDate', { format });
    else if (type === 'datetime-local' && (day.trim() || time) && (!parsed || !time || !native.value))
      error = translate(locale, 'dateInput.invalidTime');
    else if (native.validity.rangeUnderflow)
      error = translate(locale, 'dateInput.minimum', { value: displayed(min ?? '', format, locale).day + (type === 'datetime-local' ? ` ${min?.split('T')[1] ?? ''}` : '') });
    else if (native.validity.rangeOverflow)
      error = translate(locale, 'dateInput.maximum', { value: displayed(max ?? '', format, locale).day + (type === 'datetime-local' ? ` ${max?.split('T')[1] ?? ''}` : '') });
    else if (native.validity.stepMismatch) error = translate(locale, 'dateInput.interval');
    else error = native.validationMessage;
    input.current?.setCustomValidity(error);
    setMessage(error);
  }, [canonical, day, parsed, time, wire, type, required, min, max, step, format, locale]);

  function change(nextDay: string, nextTime: string) {
    setDay(nextDay); setTime(nextTime);
    const date = parseBusinessDayInput(nextDay, format, locale);
    const next = date && (type === 'date' || nextTime)
      ? type === 'date' ? date : `${date}T${nextTime}`
      : '';
    if (next !== lastEmitted.current) {
      lastEmitted.current = next;
      if (value === undefined) setInternal(next);
      onValueChange?.(next);
    }
  }

  const close = useCallback(() => { setOpen(false); input.current?.focus(); }, []);
  const showError = touched && !!message && !disabled && !readOnly;
  return (
    <span className="tl-date-input">
      <span className="tl-date-input__controls" style={{ width: style?.width }}>
        <input {...rest} id={inputId} type="text" value={day}
          ref={(element) => {
            input.current = element;
            if (typeof forwardedRef === 'function') forwardedRef(element);
            else if (forwardedRef) forwardedRef.current = element;
          }}
          className={className} style={{ ...style, minWidth: 0 }}
          placeholder={rest.placeholder ? `${rest.placeholder} (${format})` : format} data-date-format={format}
          data-date-kind={type} data-date-locale={locale} required={required} disabled={disabled} readOnly={readOnly}
          aria-label={rest['aria-label'] ?? (fieldName || undefined)}
          aria-describedby={[describedBy, showError ? errorId : undefined].filter(Boolean).join(' ') || undefined}
          aria-invalid={showError || rest['aria-invalid']}
          onChange={(event) => change(event.target.value, time)}
          onBlur={(event) => {
            setTouched(true);
            if (parsed) setDay(formatBusinessDay(parsed, format, locale));
            onBlur?.(event);
          }}
          onInvalid={(event) => { setTouched(true); onInvalid?.(event); }}
        />
        {type === 'datetime-local' ? <input type="time" value={time} step="any"
          aria-label={fieldName ? translate(locale, 'dateInput.timeFor', { field: fieldName }) : translate(locale, 'dateInput.time')}
          disabled={disabled} readOnly={readOnly}
          onChange={(event) => change(day, event.target.value)} /> : null}
        <button type="button" className="tl-iconbutton" disabled={disabled || readOnly}
          aria-label={fieldName ? translate(locale, 'dateInput.chooseFor', { field: fieldName }) : translate(locale, 'dateInput.choose')}
          aria-haspopup="dialog"
          aria-expanded={open} aria-controls={open ? calendarId : undefined}
          onClick={(event) => { event.preventDefault(); setOpen(true); }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
            <rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 11h18M8 3v4M16 3v4" />
          </svg>
        </button>
      </span>
      {name ? <input type="hidden" name={name} value={wire} disabled={disabled} /> : null}
      {showError ? <small id={errorId} role="alert" className="tl-date-input__error">{message}</small> : null}
      {open && !disabled && !readOnly ? createPortal(
        <div ref={dialog} onKeyDown={(event) => {
          if (event.key !== 'Tab') return;
          const buttons = Array.from(dialog.current?.querySelectorAll<HTMLButtonElement>('button:not([disabled])') ?? [])
            .filter((button) => button.tabIndex >= 0);
          const target = event.shiftKey ? buttons[buttons.length - 1] : buttons[0];
          if ((event.shiftKey && document.activeElement === buttons[0]) ||
              (!event.shiftKey && document.activeElement === buttons[buttons.length - 1])) {
            event.preventDefault(); target?.focus();
          }
        }}>
          <Modal id={calendarId} open onClose={close} size="sm"
            title={fieldName ? translate(locale, 'dateInput.chooseFor', { field: fieldName }) : translate(locale, 'dateInput.choose')}
            closeLabel={translate(locale, 'common.cancel')}>
            <Calendar selected={parsed ?? ''} min={min?.split('T')[0]} max={max?.split('T')[0]}
              onSelect={(date) => { change(formatBusinessDay(date, format, locale), time); close(); }} />
          </Modal>
        </div>, document.body,
      ) : null}
    </span>
  );
});
